import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  AddOutlined,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Stack,
} from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid';
import type {
  WorkflowTemplate,
  WorkflowTemplateCreateInput,
} from '../model';
import type { WorkflowPort } from '../port';
import { useAsyncResource } from '../../../shared/useAsyncResource';
import { useUnsavedChangesGuard } from '../../../shared/useUnsavedChangesGuard';
import {
  FeedbackSnackbar,
  ConfirmDialog,
  ResourcePage,
  PillTabs,
  type PillTabItem,
  ResourceCollection,
  type ResourceCollectionState,
  ValueBadge,
  useConfirmation,
} from '../../../design-system';
import {
  WorkflowApprovalSettingsPage,
  type WorkflowApprovalRoleOption,
  type WorkflowApprovalSettingsPort,
  type WorkflowApprovalSettingsSnapshot,
} from '../approval-settings/public';
import {
  WorkflowPersonPicker,
  WorkflowRolePicker,
} from '../WorkflowReferencePickers';
import { resolveWorkflowTemplateIconKey } from '../workflowTemplateIcons';
import {
  type EmployeeDirectoryPort,
  type EmployeeDirectorySnapshot,
} from '../../people/public';
import { TemplateEditorPage } from '../components/WorkflowTemplateEditor';
import { WorkflowTemplateDetailPage } from './WorkflowTemplateDetailPage';
import {
  emptyTemplateDraft,
  workflowTemplateMutationFingerprint,
  workflowTemplateToDraft,
  workflowTemplateVersionMatchesDraft,
  workflowTemplateVersionsEquivalent,
} from '../models/workflowTemplatePageModel';
import {
  fixtureApprovalLabels,
  fixtureApprovalTemplates,
  fixtureTemplates,
  fixtureWorkflowApprovalRoles,
  fixtureWorkflowEmployeeDirectory,
} from '../fixtures/workflowPageFixtures';
import { Status } from '../components/WorkflowStatus';
import {
  isAmbiguousApiMutationFailure,
  type WorkflowAccessProps,
  WorkflowMutationError,
  workflowErrorCode,
} from '../workflowPageSupport';
import { workflowPersonOptions } from '../models/workflowRequestPageModel';

type WorkflowSettingsView = 'forms' | 'templates' | 'labels';

export type WorkflowTemplatesPageProps = {
  port: WorkflowPort;
  approvalSettingsPort: WorkflowApprovalSettingsPort;
  directoryPort: EmployeeDirectoryPort;
  fixtureMode: boolean;
  allowGeneralTemplates?: boolean;
} & WorkflowAccessProps;

const workflowSettingsTabs: Array<PillTabItem<WorkflowSettingsView>> = [
  { value: 'forms', label: '워크플로우 양식', tabId: 'workflow-settings-forms-tab', panelId: 'workflow-settings-forms' },
  { value: 'templates', label: '승인 템플릿', tabId: 'workflow-settings-templates-tab', panelId: 'workflow-settings-templates' },
  { value: 'labels', label: '승인 라벨', tabId: 'workflow-settings-labels-tab', panelId: 'workflow-settings-labels' },
];

export function WorkflowTemplatesPage({ port, approvalSettingsPort, directoryPort, fixtureMode, readOnly, scopes, allowGeneralTemplates = true }: WorkflowTemplatesPageProps) {
  const canWrite = !readOnly && scopes.includes('workflow.template.write');
  const canPublish = !readOnly && scopes.includes('workflow.template.publish');
  const resource = useAsyncResource(() => !allowGeneralTemplates ? Promise.resolve([] as WorkflowTemplate[]) : fixtureMode ? Promise.resolve(fixtureTemplates) : port.listWorkflowTemplates(true).then((response) => response.data), [port, fixtureMode, allowGeneralTemplates]);
  const approvalSettings = useAsyncResource<WorkflowApprovalSettingsSnapshot>(
    () => fixtureMode
      ? Promise.resolve({ labels: fixtureApprovalLabels, templates: fixtureApprovalTemplates })
      : approvalSettingsPort.loadSnapshot(),
    [approvalSettingsPort, fixtureMode],
  );
  const directory = useAsyncResource<EmployeeDirectorySnapshot>(
    () => fixtureMode ? Promise.resolve(fixtureWorkflowEmployeeDirectory) : directoryPort.loadDirectory(),
    [directoryPort, fixtureMode],
  );
  const approvalRoleOptions = useAsyncResource<WorkflowApprovalRoleOption[]>(
    () => fixtureMode
      ? Promise.resolve(fixtureWorkflowApprovalRoles)
      : approvalSettingsPort.listRoleOptions('', 100),
    [approvalSettingsPort, fixtureMode],
  );
  const loadRoleOptions = useCallback((query: string) => fixtureMode
    ? Promise.resolve(fixtureWorkflowApprovalRoles.filter((role) => `${role.descriptor} ${role.roleCode}`.toLowerCase().includes(query.toLowerCase())))
    : approvalSettingsPort.listRoleOptions(query, 100), [approvalSettingsPort, fixtureMode]);
  const loadAccessRoleOptions = useCallback((query: string) => fixtureMode
    ? Promise.resolve(fixtureWorkflowApprovalRoles.filter((role) => `${role.descriptor} ${role.roleCode}`.toLowerCase().includes(query.toLowerCase())))
    : approvalSettingsPort.listRoleOptions(query, 100, true), [approvalSettingsPort, fixtureMode]);
  const personOptions = useMemo(() => workflowPersonOptions(directory.data), [directory.data]);
  const [requestedSettingsView, setSettingsView] = useState<WorkflowSettingsView>('forms');
  const settingsView = !allowGeneralTemplates && requestedSettingsView === 'forms' ? 'templates' : requestedSettingsView;
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<WorkflowTemplate>();
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<WorkflowTemplate>();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [editNotice, setEditNotice] = useState('');
  const [editConflict, setEditConflict] = useState(false);
  const [recoveringConflict, setRecoveringConflict] = useState(false);
  const [draft, setDraft] = useState<WorkflowTemplateCreateInput>(() => emptyTemplateDraft());
  const [templates, setTemplates] = useState<WorkflowTemplate[]>([]);
  const [approvalSettingsSnapshot, setApprovalSettingsSnapshot] = useState<WorkflowApprovalSettingsSnapshot>({ labels: [], templates: [] });
  const approvalLabels = approvalSettingsSnapshot.labels;
  const approvalTemplates = approvalSettingsSnapshot.templates;
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [statusSaving, setStatusSaving] = useState(false);
  const [statusError, setStatusError] = useState('');
  const [statusConflict, setStatusConflict] = useState(false);
  const [statusNotice, setStatusNotice] = useState('');
  const [statusRecoveryNotice, setStatusRecoveryNotice] = useState('');
  const { confirm, confirmationDialog } = useConfirmation();
  const templateMutationKeysRef = useRef(new Map<string, string>());
  const listTitleRef = useRef<HTMLHeadingElement>(null);
  const listContainerRef = useRef<HTMLDivElement>(null);
  const formsRef = useRef<HTMLDivElement>(null);
  const selectedRowFocusRef = useRef<{ id: string; field: string } | null>(null);
  const rememberRowFocus = (event: { target: EventTarget | null }) => {
    const cell = event.target instanceof Element ? event.target.closest<HTMLElement>('[role="gridcell"]') : null;
    const id = cell?.closest<HTMLElement>('[role="row"][data-id]')?.dataset.id;
    const field = cell?.dataset.field;
    if (id && field) selectedRowFocusRef.current = { id, field };
  };
  const restoreListFocusRef = useRef(false);
  const draftDirty = editing
    ? editing.displayName !== draft.displayName.trim()
      || (editing.category ?? '') !== (draft.category ?? '').trim()
      || resolveWorkflowTemplateIconKey(editing) !== draft.iconKey
      || !workflowTemplateVersionMatchesDraft(editing, draft)
    : workflowTemplateMutationFingerprint('draft', 'new', draft) !== workflowTemplateMutationFingerprint('draft', 'new', emptyTemplateDraft());
  useUnsavedChangesGuard({ dirty: editorOpen && draftDirty, busy: saving || recoveringConflict });
  useEffect(() => {
    const data = resource.data;
    if (!data) return;
    setTemplates(data);
    setSelected((current) => current ? data.find((template) => template.id === current.id) : current);
  }, [resource.data]);
  useEffect(() => {
    const data = approvalSettings.data;
    if (!data) return;
    setApprovalSettingsSnapshot({
      labels: data.labels.filter((label) => Boolean(label.id) && label.status !== 'ARCHIVED'),
      templates: data.templates.filter((template) => template.status !== 'ARCHIVED'),
    });
  }, [approvalSettings.data]);
  const normalizedQuery = query.trim().toLowerCase();
  const rows = useMemo(() => templates.filter((item) => `${item.displayName} ${item.code} ${item.category ?? ''}`.toLowerCase().includes(normalizedQuery)), [normalizedQuery, templates]);
  useEffect(() => {
    if (editorOpen || selected || !restoreListFocusRef.current || resource.loading) return;
    const forms = formsRef.current;
    if (!forms) return;
    const listContainer = listContainerRef.current;
    const remembered = selectedRowFocusRef.current;
    const restoreRow = remembered && rows.some((row) => row.id === remembered.id);
    let frame: number | undefined;
    let stopped = false;
    let restoredTarget: HTMLElement | null = null;
    const stop = () => {
      stopped = true;
      if (frame !== undefined) window.cancelAnimationFrame(frame);
      mutations.disconnect();
      resize.disconnect();
      document.removeEventListener('pointerdown', abandon, true);
      document.removeEventListener('keydown', abandon, true);
      document.removeEventListener('focusin', onFocusIn, true);
      forms.removeEventListener('focusout', schedule);
      listContainer?.removeEventListener('transitionend', schedule);
    };
    const abandon = () => { restoreListFocusRef.current = false; stop(); };
    const onFocusIn = (event: FocusEvent) => {
      if (event.target !== restoredTarget) abandon();
    };
    const schedule = () => {
      if (stopped || frame !== undefined) return;
      frame = window.requestAnimationFrame(() => {
        frame = undefined;
        // 가상화로 셀이 교체될 수 있어 저장한 DOM 대신 현재 행·열을 찾습니다.
        const target = restoreRow
          ? forms.querySelector<HTMLElement>(`[role="row"][data-id="${CSS.escape(remembered.id)}"] [role="gridcell"][data-field="${CSS.escape(remembered.field)}"]`)
          : listTitleRef.current;
        if (!target || target.closest('[inert]') || target.getClientRects().length === 0 || getComputedStyle(target).visibility !== 'visible') return;
        restoredTarget = target;
        if (document.activeElement === target) return;
        target.focus({ preventScroll: true });
      });
    };
    // 최초 복원 뒤 가상 셀이 교체되거나 초점이 body로 빠져도 다시 연결합니다.
    // 새 사용자 입력이나 다른 요소로의 초점 이동이 오면 복원을 종료합니다.
    const mutations = new MutationObserver(schedule);
    const resize = new ResizeObserver(schedule);
    mutations.observe(forms, { childList: true, subtree: true });
    resize.observe(forms);
    document.addEventListener('pointerdown', abandon, true);
    document.addEventListener('keydown', abandon, true);
    document.addEventListener('focusin', onFocusIn, true);
    forms.addEventListener('focusout', schedule);
    // 모션 축소 설정에서도 visibility 전환이 끝난 뒤에야 목록에 초점을 줄 수 있습니다.
    listContainer?.addEventListener('transitionend', schedule);
    schedule();
    return stop;
  }, [editorOpen, selected, resource.loading, rows]);
  const columns: GridColDef<WorkflowTemplate>[] = [
    { field: 'displayName', headerName: '양식', flex: 1, minWidth: 220 },
    { field: 'code', headerName: '참조 코드', width: 180 },
    { field: 'category', headerName: '분류', width: 130, valueFormatter: (value) => value || '-', renderCell: ({ value }) => value ? <ValueBadge label={value} /> : '-' },
    { field: 'fieldCount', headerName: '필드', width: 80, type: 'number', valueGetter: (_value, row) => row.version.fields.length },
    { field: 'approvalCount', headerName: '결재', width: 80, type: 'number', valueGetter: (_value, row) => row.version.approvalSteps.length },
    { field: 'versionStatus', headerName: '버전 상태', width: 110, valueGetter: (_value, row) => row.version.status, renderCell: ({ value }) => <Status value={value} /> },
  ];
  const openEditor = (template?: WorkflowTemplate) => {
    if (!canWrite) return;
    templateMutationKeysRef.current.clear();
    setEditing(template);
    setDraft(template ? workflowTemplateToDraft(template) : emptyTemplateDraft());
    setError('');
    setEditNotice('');
    setEditConflict(false);
    setEditorOpen(true);
  };
  const closeEditor = async () => {
    if (saving || recoveringConflict) return;
    if (draftDirty && !await confirm({
      intent: 'discard',
      title: '수정한 내용을 버릴까요?',
      description: '저장하지 않은 양식 변경을 버리고 목록으로 돌아갑니다.',
      confirmLabel: '버리고 이동',
      cancelLabel: '계속 수정',
    })) return;
    templateMutationKeysRef.current.clear();
    restoreListFocusRef.current = true;
    setEditorOpen(false);
    setEditing(undefined);
    setSelected(undefined);
    setEditConflict(false);
    setError('');
    setEditNotice('');
  };
  const duplicateTemplate = (template: WorkflowTemplate) => {
    if (!canWrite) return;
    templateMutationKeysRef.current.clear();
    setEditing(undefined);
    setError('');
    setEditNotice('');
    setEditConflict(false);
    setDraft({
      displayName: `${template.displayName} 복사본`,
      category: template.category,
      iconKey: resolveWorkflowTemplateIconKey(template),
      description: template.version.description,
      fields: template.version.fields.map(({ id: _id, ...field }) => ({ ...field, config: field.config ? structuredClone(field.config) : undefined, validation: field.validation ? structuredClone(field.validation) : undefined })),
      approvalSteps: template.version.approvalSteps.map(({ id: _id, ...step }) => ({ ...step, resolverConfig: step.resolverConfig ? structuredClone(step.resolverConfig) : undefined })),
      accessRules: template.version.accessRules.map(({ id: _id, ...rule }) => ({ ...rule })),
    });
    setSelected(undefined);
    setEditorOpen(true);
  };
  const mutationKeyFor = (fingerprint: string): string => {
    const existing = templateMutationKeysRef.current.get(fingerprint);
    if (existing) return existing;
    const key = crypto.randomUUID();
    templateMutationKeysRef.current.set(fingerprint, key);
    return key;
  };
  const rememberSavedTemplate = (template: WorkflowTemplate) => {
    setEditing(template);
    setSelected(template);
    setTemplates((current) => current.some((item) => item.id === template.id)
      ? current.map((item) => item.id === template.id ? template : item)
      : [...current, template]);
  };
  const saveTemplate = async (publish: boolean) => {
    if (!canWrite || (publish && !canPublish)) return;
    setSaving(true); setError(''); setEditNotice(''); setEditConflict(false);
    let lastSuccessfulStage: 'none' | 'metadata' | 'version' = 'none';
    let failedStage: 'create' | 'metadata' | 'version' | 'publish' | undefined;
    let failedIntentFingerprint: string | undefined;
    let draftContentSaved = Boolean(editing
      && editing.version.status === 'DRAFT'
      && workflowTemplateVersionMatchesDraft(editing, draft));
    try {
      if (fixtureMode) {
        setEditorOpen(false); setDraft(emptyTemplateDraft()); setEditing(undefined); resource.reload(); return;
      }
      let saved: WorkflowTemplate;
      if (!editing) {
        failedStage = 'create';
        failedIntentFingerprint = workflowTemplateMutationFingerprint('create', 'new', draft);
        saved = await port.createWorkflowTemplate(draft, mutationKeyFor(failedIntentFingerprint));
        // A successful create owns a durable aggregate even when replay reconciliation below finds a conflict.
        // Remember its ID/revision immediately so retry can never create a duplicate and recovery can refetch it.
        rememberSavedTemplate(saved);
        if (saved.editRevision !== 1
          || saved.displayName !== draft.displayName.trim()
          || (saved.category ?? '') !== (draft.category ?? '').trim()
          || resolveWorkflowTemplateIconKey(saved) !== draft.iconKey
          || saved.version.status !== 'DRAFT'
          || !workflowTemplateVersionMatchesDraft(saved, draft)) {
          throw new WorkflowMutationError(409, '생성된 양식이 다른 곳에서 변경되었습니다.', 'WORKFLOW_TEMPLATE_EDIT_CONFLICT');
        }
        templateMutationKeysRef.current.delete(failedIntentFingerprint);
        failedIntentFingerprint = undefined;
        lastSuccessfulStage = 'version';
        draftContentSaved = true;
      } else {
        const desiredDisplayName = draft.displayName.trim();
        const desiredCategory = (draft.category ?? '').trim();
        const desiredIconKey = draft.iconKey;
        const metadataChanged = editing.displayName !== desiredDisplayName
          || (editing.category ?? '') !== desiredCategory
          || resolveWorkflowTemplateIconKey(editing) !== desiredIconKey;
        saved = editing;
        if (metadataChanged) {
          const metadataInput = {
            displayName: desiredDisplayName,
            category: desiredCategory,
            iconKey: desiredIconKey,
            status: editing.status,
            expectedRevision: editing.editRevision,
          };
          failedStage = 'metadata';
          failedIntentFingerprint = workflowTemplateMutationFingerprint('metadata', editing.id, metadataInput);
          saved = await port.updateWorkflowTemplate(editing.id, metadataInput, mutationKeyFor(failedIntentFingerprint));
          if (saved.editRevision !== metadataInput.expectedRevision + 1
            || saved.displayName !== desiredDisplayName
            || (saved.category ?? '') !== desiredCategory
            || resolveWorkflowTemplateIconKey(saved) !== desiredIconKey
            || !workflowTemplateVersionsEquivalent(saved, editing)) {
            throw new WorkflowMutationError(409, '다른 관리자의 최신 변경과 충돌했습니다.', 'WORKFLOW_TEMPLATE_EDIT_CONFLICT');
          }
          templateMutationKeysRef.current.delete(failedIntentFingerprint);
          failedIntentFingerprint = undefined;
          lastSuccessfulStage = 'metadata';
          rememberSavedTemplate(saved);
        }
        const versionAlreadySaved = saved.version.status === 'DRAFT'
          && workflowTemplateVersionMatchesDraft(saved, draft);
        if (!versionAlreadySaved) {
          const versionInput = {
            description: draft.description,
            fields: draft.fields,
            approvalSteps: draft.approvalSteps,
            accessRules: draft.accessRules,
            expectedRevision: saved.editRevision,
          };
          const versionMutation = saved.version.status === 'DRAFT' ? 'version-update' : 'version-create';
          failedStage = 'version';
          failedIntentFingerprint = workflowTemplateMutationFingerprint(versionMutation, saved.version.status === 'DRAFT' ? saved.version.id : saved.id, versionInput);
          saved = saved.version.status === 'DRAFT'
            ? await port.updateWorkflowTemplateVersion(saved.version.id, versionInput, mutationKeyFor(failedIntentFingerprint))
            : await port.createWorkflowTemplateVersion(saved.id, versionInput, mutationKeyFor(failedIntentFingerprint));
          if (saved.editRevision !== versionInput.expectedRevision + 1
            || saved.version.status !== 'DRAFT'
            || !workflowTemplateVersionMatchesDraft(saved, draft)) {
            throw new WorkflowMutationError(409, '다른 관리자의 최신 변경과 충돌했습니다.', 'WORKFLOW_TEMPLATE_EDIT_CONFLICT');
          }
          templateMutationKeysRef.current.delete(failedIntentFingerprint);
          failedIntentFingerprint = undefined;
          lastSuccessfulStage = 'version';
          draftContentSaved = true;
          rememberSavedTemplate(saved);
        } else {
          draftContentSaved = true;
        }
      }
      if (publish) {
        const publishInput = { expectedRevision: saved.editRevision };
        failedStage = 'publish';
        failedIntentFingerprint = workflowTemplateMutationFingerprint('publish', saved.version.id, publishInput);
        saved = await port.publishWorkflowTemplateVersion(saved.version.id, saved.editRevision, mutationKeyFor(failedIntentFingerprint));
        if (saved.editRevision !== publishInput.expectedRevision + 1 || saved.version.status !== 'PUBLISHED') {
          throw new WorkflowMutationError(409, '게시 상태가 다른 곳에서 변경되었습니다.', 'WORKFLOW_TEMPLATE_EDIT_CONFLICT');
        }
        templateMutationKeysRef.current.delete(failedIntentFingerprint);
        failedIntentFingerprint = undefined;
        rememberSavedTemplate(saved);
      }
      templateMutationKeysRef.current.clear();
      setEditorOpen(false); setDraft(emptyTemplateDraft()); setEditing(undefined); setSelected(saved); resource.reload();
    } catch (reason) {
      if (failedIntentFingerprint && !isAmbiguousApiMutationFailure(reason)) {
        templateMutationKeysRef.current.delete(failedIntentFingerprint);
      }
      const conflict = workflowErrorCode(reason) === 'WORKFLOW_TEMPLATE_EDIT_CONFLICT';
      setEditConflict(conflict);
      setEditNotice('');
      const partialMessage = failedStage === 'publish' && draftContentSaved
        ? '초안 내용은 저장되었지만 게시하지 못했습니다.'
        : failedStage === 'version' && lastSuccessfulStage === 'metadata'
          ? '양식 기본 정보는 저장되었지만 초안 내용은 저장하지 못했습니다.'
          : '';
      const recoveryMessage = conflict
        ? '다른 관리자의 최신 변경과 충돌했습니다. 최신 양식을 불러온 후 다시 시도해 주세요.'
        : partialMessage
          ? '같은 저장 버튼을 누르면 완료된 단계 다음부터 다시 시도합니다.'
          : reason instanceof Error ? reason.message : '양식을 저장하지 못했습니다.';
      setError([partialMessage, recoveryMessage].filter(Boolean).join(' '));
    } finally { setSaving(false); }
  };
  const reloadLatestTemplate = async () => {
    if (!editing || recoveringConflict) return;
    if (!await confirm({
      intent: 'discard',
      title: '최신 양식을 불러올까요?',
      description: '현재 화면의 저장되지 않은 변경을 버리고 서버에 저장된 최신 양식을 불러옵니다.',
      confirmLabel: '버리고 불러오기',
      cancelLabel: '계속 수정',
    })) return;
    templateMutationKeysRef.current.clear();
    setRecoveringConflict(true);
    try {
      const latest = await port.getWorkflowTemplate(editing.id);
      setEditing(latest);
      setSelected(latest);
      setDraft(workflowTemplateToDraft(latest));
      setEditConflict(false);
      setError('');
      setEditNotice('최신 양식을 불러왔습니다. 변경된 내용을 확인한 후 다시 저장해 주세요.');
      resource.reload();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : '최신 양식을 불러오지 못했습니다.');
    } finally {
      setRecoveringConflict(false);
    }
  };
  const openStatusDialog = () => {
    if (!canWrite || !selected) return;
    setStatusError('');
    setStatusRecoveryNotice('');
    setStatusConflict(false);
    setStatusDialogOpen(true);
  };
  const changeTemplateStatus = async () => {
    if (!canWrite || !selected || selected.status !== 'ACTIVE') return;
    const target = selected;
    setStatusSaving(true); setStatusError(''); setStatusConflict(false);
    try {
      const updated: WorkflowTemplate = fixtureMode
        ? { ...target, status: 'ARCHIVED', editRevision: target.editRevision + 1, updatedAt: new Date().toISOString() }
        : await port.updateWorkflowTemplate(target.id, {
          displayName: target.displayName,
          category: target.category,
          iconKey: resolveWorkflowTemplateIconKey(target),
          status: 'ARCHIVED',
          expectedRevision: target.editRevision,
        });
      setTemplates((current) => current.filter((template) => template.id !== updated.id));
      setSelected(undefined);
      setStatusDialogOpen(false);
      setStatusNotice(`“${target.displayName}” 양식을 보관했습니다. 기존 요청과 이력은 유지됩니다.`);
    } catch (reason) {
      const conflict = workflowErrorCode(reason) === 'WORKFLOW_TEMPLATE_EDIT_CONFLICT';
      setStatusConflict(conflict);
      setStatusError(conflict
        ? '다른 관리자가 이 양식을 변경했습니다. 최신 상태를 확인한 후 다시 시도해 주세요.'
        : reason instanceof Error ? reason.message : '양식 상태를 변경하지 못했습니다.');
    } finally { setStatusSaving(false); }
  };
  const reloadSelectedTemplate = async () => {
    if (!selected || statusSaving) return;
    setStatusSaving(true);
    try {
      const latest = await port.getWorkflowTemplate(selected.id);
      setSelected(latest);
      setTemplates((current) => current.map((template) => template.id === latest.id ? latest : template));
      setStatusDialogOpen(false);
      setStatusConflict(false);
      setStatusError('');
      setStatusRecoveryNotice(`“${latest.displayName}” 양식의 최신 상태를 불러왔습니다. 내용을 확인한 후 다시 시도해 주세요.`);
      resource.reload();
    } catch (reason) {
      setStatusError(reason instanceof Error ? reason.message : '최신 양식 상태를 불러오지 못했습니다.');
    } finally {
      setStatusSaving(false);
    }
  };
  const showEditor = allowGeneralTemplates && editorOpen && canWrite;
  const listHidden = showEditor || (allowGeneralTemplates && Boolean(selected));
  const templateEditor = showEditor ? <TemplateEditorPage
      value={draft}
      editing={editing}
      approvalLabels={[
        { name: '승인', tone: 'GREEN' },
        ...approvalLabels,
      ]}
      approvalTemplates={approvalTemplates}
      personOptions={personOptions}
      organizationOptions={directory.data?.organizations ?? []}
      personOptionsLoading={directory.loading}
      personOptionsError={directory.error?.message}
      onRetryPersonOptions={directory.reload}
      loadRoleOptions={loadRoleOptions}
      loadAccessRoleOptions={loadAccessRoleOptions}
      roleOptions={approvalRoleOptions.data ?? []}
      roleOptionsLoading={approvalRoleOptions.loading}
      roleOptionsError={approvalRoleOptions.error?.message}
      onRetryRoleOptions={approvalRoleOptions.reload}
      onChange={setDraft}
      onClose={() => void closeEditor()}
      onSubmit={(publish) => void saveTemplate(publish)}
      saving={saving}
      error={error}
      notice={editNotice}
      editConflict={editConflict}
      recoveringConflict={recoveringConflict}
      onReloadLatest={() => void reloadLatestTemplate()}
      canPublish={canPublish}
    /> : null;
  const collectionState: ResourceCollectionState = resource.loading
    ? { kind: 'loading' }
    : resource.error
      ? { kind: 'error', message: resource.error.message, onRetry: resource.reload }
      : rows.length === 0
        ? normalizedQuery
          ? { kind: 'empty', title: '검색 결과가 없습니다.', description: '다른 양식명, 코드 또는 분류로 검색해 주세요.', action: <Button variant="weak" onClick={() => setQuery('')}>검색 초기화</Button> }
          : { kind: 'empty', title: '등록된 워크플로우 양식이 없습니다.' }
        : { kind: 'ready' };
  return <Box sx={{ position: 'relative', height: '100%', minHeight: 0, minWidth: 0 }}>
    {templateEditor}
    {allowGeneralTemplates && selected && !showEditor && <WorkflowTemplateDetailPage
      template={selected}
      canWrite={canWrite}
      personOptions={personOptions}
      organizationOptions={directory.data?.organizations ?? []}
      personOptionsLoading={directory.loading}
      personOptionsError={directory.error?.message}
      onRetryPersonOptions={directory.reload}
      notice={statusRecoveryNotice}
      onDismissNotice={() => setStatusRecoveryNotice('')}
      onBack={() => { restoreListFocusRef.current = true; setSelected(undefined); }}
      onEdit={() => { openEditor(selected); setSelected(undefined); }}
      onDuplicate={() => duplicateTemplate(selected)}
      onArchive={openStatusDialog}
    />}
    <Box ref={listContainerRef} inert={listHidden || undefined} aria-hidden={listHidden || undefined} sx={{ height: '100%', minHeight: 0, minWidth: 0, position: listHidden ? 'absolute' : 'relative', inset: listHidden ? 0 : 'auto', visibility: listHidden ? 'hidden' : 'visible' }}>
    <ResourcePage
      title={allowGeneralTemplates ? "워크플로우 설정" : "승인 설정"}
      titleRef={listTitleRef}
      primaryAction={settingsView === 'forms' && canWrite ? <Button variant="fill" startIcon={<AddOutlined />} onClick={() => openEditor()}>새 양식</Button> : undefined}
      tabs={<PillTabs value={settingsView} items={workflowSettingsTabs.filter((tab) => allowGeneralTemplates || tab.value !== 'forms')} ariaLabel="워크플로우 설정 메뉴" onChange={setSettingsView} />}
    >
      <Stack sx={{ position: 'relative', height: '100%', minHeight: 0 }}>
        <Box
          ref={formsRef}
          id="workflow-settings-forms"
          role="tabpanel"
          aria-labelledby="workflow-settings-forms-tab"
          onClickCapture={rememberRowFocus}
          onKeyDownCapture={rememberRowFocus}
          inert={settingsView !== 'forms' || undefined}
          aria-hidden={settingsView !== 'forms' || undefined}
          sx={{ flex: 1, minHeight: 0, position: settingsView !== 'forms' ? 'absolute' : 'relative', inset: settingsView !== 'forms' ? 0 : 'auto', visibility: settingsView !== 'forms' ? 'hidden' : 'inherit' }}
        >
          <Box sx={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            <FeedbackSnackbar open={Boolean(statusNotice)} message={statusNotice} tone="success" onClose={() => setStatusNotice('')} />
            {statusRecoveryNotice ? <Alert severity="info" onClose={() => setStatusRecoveryNotice('')} sx={{ mb: 1.5, flexShrink: 0 }}>{statusRecoveryNotice}</Alert> : null}
            <Box sx={{ flex: 1, minHeight: 0 }}>
              <ResourceCollection state={collectionState} rows={rows} totalCount={templates.length} columns={columns} query={query} onQueryChange={setQuery} searchPlaceholder="양식명, 코드 또는 분류 검색" onRowClick={({ row }) => setSelected(row)} />
            </Box>
          </Box>
        </Box>
        {(['templates', 'labels'] as const).map((view) => <Box
          key={view}
          id={`workflow-settings-${view}`}
          role="tabpanel"
          aria-labelledby={`workflow-settings-${view}-tab`}
          inert={settingsView !== view || undefined}
          aria-hidden={settingsView !== view || undefined}
          sx={{ flex: 1, minHeight: 0, minWidth: 0, overflow: 'auto', position: settingsView !== view ? 'absolute' : 'relative', inset: settingsView !== view ? 0 : 'auto', visibility: settingsView !== view ? 'hidden' : 'inherit' }}
        >
          <WorkflowApprovalSettingsPage
            view={view}
            templates={approvalTemplates}
            labels={approvalLabels}
            renderFixedPersonPicker={({ personID, disabled, onChange }) => <WorkflowPersonPicker
              label="구성원"
              personId={personID}
              options={personOptions}
              loading={directory.loading}
              error={directory.error?.message}
              onRetry={directory.reload}
              disabled={disabled}
              onChange={onChange}
            />}
            renderFixedRolePicker={({ roleCode, disabled, onChange }) => <WorkflowRolePicker
              loadOptions={loadRoleOptions}
              label="승인 역할"
              roleCode={roleCode}
              options={approvalRoleOptions.data ?? []}
              loading={approvalRoleOptions.loading}
              error={approvalRoleOptions.error?.message}
              onRetry={approvalRoleOptions.reload}
              disabled={disabled}
              onChange={onChange}
            />}
            loading={approvalSettings.loading}
            error={approvalSettings.error?.message}
            readOnly={!canWrite}
            onRetry={approvalSettings.reload}
            onCreateTemplate={async (input) => {
              const saved = fixtureMode
                ? (() => {
                  const { expectedVersion: _expectedVersion, ...payload } = input;
                  const now = new Date().toISOString();
                  return { ...payload, description: payload.description ?? '', id: crypto.randomUUID().replaceAll('-', ''), status: 'ACTIVE' as const, version: 1, createdAt: now, updatedAt: now };
                })()
                : await approvalSettingsPort.createTemplate(input);
              setApprovalSettingsSnapshot((current) => ({ ...current, templates: [...current.templates.filter((template) => template.id !== saved.id), saved] }));
              return saved;
            }}
            onUpdateTemplate={async (id, input) => {
              const saved = fixtureMode
                ? (() => {
                  const { expectedVersion, ...payload } = input;
                  const current = approvalTemplates.find((template) => template.id === id);
                  return {
                    ...payload,
                    description: payload.description ?? '',
                    id,
                    status: 'ACTIVE' as const,
                    version: (expectedVersion ?? current?.version ?? 0) + 1,
                    createdAt: current?.createdAt ?? new Date().toISOString(),
                    updatedAt: new Date().toISOString(),
                  };
                })()
                : await approvalSettingsPort.updateTemplate(id, input);
              setApprovalSettingsSnapshot((current) => ({ ...current, templates: [...current.templates.filter((template) => template.id !== saved.id), saved] }));
              return saved;
            }}
            onDeleteTemplate={async (id, version) => {
              if (!fixtureMode) await approvalSettingsPort.deleteTemplate(id, version);
              setApprovalSettingsSnapshot((current) => ({ ...current, templates: current.templates.filter((template) => template.id !== id) }));
            }}
            onCreateLabel={async (input) => {
              const saved = fixtureMode
                ? (() => {
                  const { expectedVersion: _expectedVersion, ...payload } = input;
                  const now = new Date().toISOString();
                  return { ...payload, id: crypto.randomUUID().replaceAll('-', ''), status: 'ACTIVE' as const, version: 1, createdAt: now, updatedAt: now, description: payload.description ?? '' };
                })()
                : await approvalSettingsPort.createLabel(input);
              setApprovalSettingsSnapshot((current) => ({ ...current, labels: [...current.labels.filter((label) => label.id !== saved.id), saved] }));
              return saved;
            }}
            onUpdateLabel={async (id, input) => {
              const saved = fixtureMode
                ? (() => {
                  const { expectedVersion, ...payload } = input;
                  const current = approvalLabels.find((label) => label.id === id);
                  return {
                    ...payload,
                    id,
                    status: 'ACTIVE' as const,
                    version: (expectedVersion ?? current?.version ?? 0) + 1,
                    createdAt: current?.createdAt ?? new Date().toISOString(),
                    updatedAt: new Date().toISOString(),
                    description: payload.description ?? '',
                  };
                })()
                : await approvalSettingsPort.updateLabel(id, input);
              setApprovalSettingsSnapshot((current) => ({ ...current, labels: [...current.labels.filter((label) => label.id !== saved.id), saved] }));
              return saved;
            }}
            onDeleteLabel={async (id, version) => {
              if (!fixtureMode) await approvalSettingsPort.deleteLabel(id, version);
              setApprovalSettingsSnapshot((current) => ({ ...current, labels: current.labels.filter((label) => label.id !== id) }));
            }}
            confirmDelete={(target) => confirm({
              intent: 'archive',
              title: `${target.kind === 'template' ? '승인 템플릿' : '승인 라벨'}을 보관할까요?`,
              description: `“${target.name}”${target.kind === 'template' ? ' 템플릿' : ' 라벨'}은 새 항목에서 선택할 수 없게 됩니다. 기존 요청과 이력은 유지됩니다.`,
              confirmLabel: target.kind === 'template' ? '템플릿 보관' : '라벨 보관',
            })}
            confirmDiscardChanges={(name) => confirm({
              intent: 'discard',
              title: '수정한 내용을 버릴까요?',
              description: `“${name}”의 저장하지 않은 변경을 버리고 다른 항목을 엽니다.`,
              confirmLabel: '버리고 이동',
              cancelLabel: '계속 수정',
            })}
          />
        </Box>)}
      </Stack>
    </ResourcePage>
    </Box>
    {canWrite && <ConfirmDialog
      open={allowGeneralTemplates && statusDialogOpen && Boolean(selected)}
      onCancel={() => { if (statusSaving) return; setStatusDialogOpen(false); setStatusError(''); setStatusConflict(false); }}
      onConfirm={changeTemplateStatus}
      intent="archive"
      title="양식 보관"
      description={`“${selected?.displayName ?? '선택한 양식'}”을 보관하면 새 요청에서 선택할 수 없습니다. 기존 요청과 버전 이력은 유지됩니다.`}
      cancelLabel="돌아가기"
      confirmLabel="양식 보관"
      busy={statusSaving}
      busyLabel="처리 중"
      confirmDisabled={statusConflict}
      error={statusError}
      errorAction={statusConflict ? <Button color="inherit" size="small" disabled={statusSaving} onClick={() => void reloadSelectedTemplate()}>최신 상태 확인</Button> : undefined}
    />}
    {confirmationDialog}
  </Box>;
}
